import { BellAlertIcon, ChatBubbleLeftEllipsisIcon } from "@heroicons/react/24/outline";
import React from "react";
import TextArea from "src/components/Shared/Forms/Inputs/TextArea";
import SelectMenu from "src/components/Shared/Forms/Selects/SelectMenu";
import Toggle from "src/components/Shared/Forms/Toggles/Toggle";
import ToggleHeader from "src/components/Shared/Forms/Toggles/ToggleHeader";
import { notificationVariants } from "src/helpers/notificationHelper";
import SiteColorItem from "../SiteColorItem";
import MultiSelectObject from "src/components/Shared/Forms/Selects/MultiSelectObject/MultiSelectObject";

const BannerDetails = ({ formData, setFormData, clients = [] }) => {
  const handleSelectedClients = (selectedItems) => {
    setFormData((prev) => ({
      ...prev,
      client_ids: selectedItems.filter((item) => item.selected).map((item) => item.key),
    }));
  };

  const handleVariantChange = (variant) => {
    const variantData = notificationVariants[variant];

    setFormData((prev) => ({
      ...prev,
      variant,
      background_color: variant === "custom" ? prev.background_color : variantData.backgroundColor,
      text_color: variant === "custom" ? prev.text_color : variantData.textColor,
    }));
  };

  const handleContentChange = (e) => {
    const newValue = e.target.value;
    setFormData((prev) => {
      if (prev.content === newValue) return prev;
      return { ...prev, content: newValue };
    });
  };

  return (
    <div className="animate-fade-in flex flex-col gap-4 pb-3">
      <div className="border-b pb-4">
        <ToggleHeader
          position="right"
          headerContainerClassName="!mb-0"
          subcontainerClasses="!justify-between !w-full"
          icon={<BellAlertIcon className="h-5 w-5" />}
          title="Enable Notification"
          subtitle={"Turn this on to mark this as your currently active notification."}>
          <div className="flex items-center justify-center gap-x-3">
            <Toggle
              checked={formData.status}
              onChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}
            />
          </div>
        </ToggleHeader>
      </div>
      <div>
        <MultiSelectObject
          inline
          defaultOptions={clients.map((client) => ({
            key: client._id || client.id,
            value: client.company_name,
            selected: formData.client_ids?.includes(client._id || client.id),
            object: client,
          }))}
          searchComponent={(object) => (
            <div className="relative flex items-center justify-center gap-x-2">
              <div>{object.company_name}</div>
            </div>
          )}
          bubbleComponent={(object) => (
            <div className="relative flex flex-col gap-x-2 px-1">
              <div className="leading-4">{object.company_name}</div>
            </div>
          )}
          searchableFields={["company_name", "email"]}
          title="Select Clients"
          placeholder={formData?.client_ids?.length > 0 ? `${formData?.client_ids?.length} clients selected` : "Search"}
          onChange={handleSelectedClients}
          showSelectedItems={false}
        />
      </div>
      <div>
        <TextArea
          inline
          styles={{ height: "100px" }}
          label="Banner Text"
          value={formData.content ?? ""}
          onChange={handleContentChange}
        />
      </div>

      <div className="w-full">
        <SelectMenu
          inline
          label="Banner Style"
          options={Object.entries(notificationVariants).map(([variant, config]) => ({
            key: variant,
            value: variant,
            label: config.label,
            icon: config.icon,
            Component: () => {
              const Icon = config.icon;
              return (
                <div className="flex items-center gap-2">
                  {Icon && (
                    <Icon
                      className="h-5 w-5 shrink-0"
                      style={{ color: config.textColor }}
                    />
                  )}
                  <span
                    className="text-sm font-medium"
                    style={{ color: config.textColor }}>
                    {config.label}
                  </span>
                </div>
              );
            },
          }))}
          startIndex={Object.keys(notificationVariants).indexOf(formData.variant)}
          setOption={(option) => handleVariantChange(option.value)}
        />
      </div>

      <div className={`relative z-0  ${formData?.variant === "custom" ? "pb-16" : ""}`}>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <ChatBubbleLeftEllipsisIcon className="h-4 w-4" />
            Banner Preview
          </label>

          <div
            className="flex rounded-lg border shadow-sm transition-all duration-200"
            style={{
              backgroundColor: formData.background_color,
              borderColor: formData.background_color === "#ffffff" ? "#e5e7eb" : formData.text_color,
            }}>
            <div
              className="w-full whitespace-pre-wrap break-all rounded-lg px-4 py-3 text-sm font-medium"
              style={{
                color: formData.text_color,
                backgroundColor: "transparent",
              }}>
              {formData.content || "No content provided."}
            </div>
          </div>
        </div>

        {formData.variant === "custom" && (
          <>
            <div className="absolute -bottom-8 left-44 z-20 flex w-[140px] flex-col rounded-xl border border-gray-100 bg-white px-2 py-2 drop-shadow-md before:absolute before:-top-2 before:left-4 before:-z-10 before:h-4 before:w-4 before:rotate-[45deg] before:border before:border-b-0 before:border-r-0 before:border-gray-100 before:bg-white before:content-['']">
              <SiteColorItem
                onChange={(value) => setFormData((prev) => ({ ...prev, background_color: value }))}
                color={formData.background_color}
                name="Background Color"
              />
            </div>
            <div className="absolute -bottom-8 left-0 z-20 flex w-[140px] flex-col rounded-xl border border-gray-100 bg-white px-2 py-2 drop-shadow-md before:absolute before:-top-2 before:left-4 before:-z-10 before:h-4 before:w-4 before:rotate-[45deg] before:border before:border-b-0 before:border-r-0 before:border-gray-100 before:bg-white before:content-['']">
              <SiteColorItem
                onChange={(value) => setFormData((prev) => ({ ...prev, text_color: value }))}
                color={formData.text_color}
                name="Text Color"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BannerDetails;
